/**
 * WordPress dependencies
 */
import { getContext, getElement, store } from '@wordpress/interactivity';

type MenuOpenedBy = {
	click: boolean;
	focus: boolean;
};

type MegaMenuContext = {
	menuOpenedBy: MenuOpenedBy;
	previousFocus?: HTMLElement | null;
	megaMenu?: HTMLElement | null;
};

const getMegaMenuContext = getContext< MegaMenuContext >;

const { state, actions } = store( 'dtn/mega-menu', {
	state: {
		get isMenuOpen(): boolean {
			// The menu is opened if either `click` or `focus` is true.
			return Object.values( state.menuOpenedBy ).filter( Boolean ).length > 0;
		},
		get menuOpenedBy(): MenuOpenedBy {
			const context = getMegaMenuContext();
			return context.menuOpenedBy;
		},
	},
	actions: {
		toggleMenuOnClick() {
			const { ref } = getElement() as { ref: HTMLElement | null };

			if ( ! ref ) {
				return;
			}

			const context = getMegaMenuContext();

			// Safari won't send focus to the clicked element, so we need to manually place it: https://bugs.webkit.org/show_bug.cgi?id=22261
			if ( window.document.activeElement !== ref ) {
				ref.focus();
			}

			if ( state.menuOpenedBy.click || state.menuOpenedBy.focus ) {
				actions.closeMenu( 'click' );
				actions.closeMenu( 'focus' );
			} else {
				context.previousFocus = ref;
				actions.openMenu( 'click' );
			}
		},
		closeMenuOnClick() {
			actions.closeMenu( 'click' );
			actions.closeMenu( 'focus' );
		},
		handleMenuKeydown( event: KeyboardEvent ) {
			if ( state.menuOpenedBy.click ) {
				// If Escape close the menu.
				if ( event?.key === 'Escape' ) {
					actions.closeMenu( 'click' );
					actions.closeMenu( 'focus' );
				}
			}
		},
		handleMenuFocusout( event: FocusEvent ) {
			const context = getMegaMenuContext();
			const menuContainer = context.megaMenu?.querySelector(
				'.wp-block-dtn-mega-menu__menu-container'
			);
			const relatedTargetNode =
				event.relatedTarget instanceof Node ? event.relatedTarget : null;
			// If focus is outside menu, and in the document, close menu
			// event.target === The element losing focus
			// event.relatedTarget === The element receiving focus (if any)
			// When focusout is outside the document,
			// `window.document.activeElement` doesn't change.

			// The event.relatedTarget is null when something outside the navigation menu is clicked. This is only necessary for Safari.
			// TODO: There is still an issue in Safari where clicking on the menu link closes the menu. We don't want this. The toggleMenuOnClick callback should handle this.
			if (
				event.relatedTarget === null ||
				( ! menuContainer?.contains( relatedTargetNode ) &&
					event.target !== window.document.activeElement )
			) {
				actions.closeMenu( 'click' );
				actions.closeMenu( 'focus' );
			}
		},
		openMenu( menuOpenedOn: keyof MenuOpenedBy = 'click' ) {
			state.menuOpenedBy[ menuOpenedOn ] = true;
		},
		closeMenu( menuClosedOn: keyof MenuOpenedBy = 'click' ) {
			const context = getMegaMenuContext();
			state.menuOpenedBy[ menuClosedOn ] = false;

			// Reset the menu reference and button focus when closed.
			if ( ! state.isMenuOpen ) {
				if ( context.megaMenu?.contains( window.document.activeElement ) ) {
					context.previousFocus?.focus();
				}
				context.previousFocus = null;
				context.megaMenu = null;
			}
		},
	},
	callbacks: {
		initMenu() {
			const { ref } = getElement() as { ref: HTMLElement | null };

			if ( ! ref ) {
				return;
			}

			const context = getMegaMenuContext();

			// Set the menu reference when initialized.
			if ( state.isMenuOpen ) {
				context.megaMenu = ref;
			}
		},
	},
} );
